import React, { useMemo, useState } from 'react';
import { api } from '../api';
import { computeSOWCosts } from '../computeSOWCosts';
import SOWCostTable from './SOWCostTable';

const SELECT = ({ value, onChange, options }) => (
  <select value={value || ''} onChange={e => onChange(e.target.value)}>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

function Field({ label, children }) {
  return (
    <div style={{marginBottom:10}}>
      <label style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',display:'block',marginBottom:3}}>{label}</label>
      {children}
    </div>
  );
}

function NumberInput({ value, onChange, placeholder }) {
  return (
    <input type="number" defaultValue={value ?? ''} placeholder={placeholder || ''}
      onBlur={e => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
      style={{ width: '100%' }} />
  );
}

function CheckRow({ label, checked, onChange }) {
  return (
    <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13,marginBottom:8}}>
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked ? 1 : 0)}
        style={{width:15,height:15,accentColor:'var(--brand)'}} />
      {label}
    </label>
  );
}

function SectionHeader({ label }) {
  return (
    <div style={{gridColumn:'span 2',marginTop:12,marginBottom:6,
      fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.06em',
      color:'var(--brand)',borderBottom:'1px solid var(--border)',paddingBottom:4}}>
      {label}
    </div>
  );
}

const CONDITION = [['','— Select —'],['good','Good'],['fair','Fair'],['poor','Poor']];
const ACTION_KEEP = [['keep','Keep'],['repair','Repair'],['replace','Replace']];

export default function ExteriorSection({ deal, onUpdate, statusLabel, statusColor }) {
  const [data, setData] = useState(deal.sow_exterior || {});
  const [open, setOpen] = useState(false);

  const costLines = useMemo(() => {
    const all = computeSOWCosts({ ...deal, sow_exterior: data });
    return all.filter(l => l.section.startsWith('exterior'));
  }, [deal, data]);

  const save = async (field, value) => {
    const updated = { ...data, [field]: value };
    setData(updated);
    await api.updateSowExterior(deal.id, { [field]: value });
  };

  const summaryParts = [
    data.window_count && `${data.window_count} windows`,
    data.siding_action && data.siding_action !== 'keep' && `Siding: ${data.siding_action}`,
    data.ext_paint_action && data.ext_paint_action !== 'keep' && `Paint: ${data.ext_paint_action}`,
    data.gutters_action && !['keep','none',''].includes(data.gutters_action) && `Gutters: ${data.gutters_action}`,
    data.pressure_wash && 'Pressure wash',
  ].filter(Boolean);

  return (
    <div className="card" style={{marginBottom:16,padding:0,overflow:'hidden'}}>
      <div style={{display:'flex',alignItems:'center',gap:10,padding:'12px 16px',cursor:'pointer',
        borderLeft:'4px solid #27ae60'}} onClick={() => setOpen(o => !o)}>
        <div style={{flex:1}}>
          <div style={{fontWeight:600,fontSize:14}}>Exterior</div>
          <div style={{fontSize:12,color:'var(--text-muted)',marginTop:2}}>
            {summaryParts.length ? summaryParts.join(' · ') : 'Windows · Siding · Paint · Gutters'}
          </div>
        </div>
        {statusLabel && (
          <span style={{fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:12,
            background:`${statusColor}22`,color:statusColor,whiteSpace:'nowrap'}}>
            {statusLabel}
          </span>
        )}
        <span style={{color:'var(--text-muted)',fontSize:16}}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{padding:'16px',borderTop:'1px solid var(--bg)'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 20px'}}>

            {/* ── Windows ── */}
            <SectionHeader label="Windows" />
            <Field label="Window Count">
              <NumberInput value={data.window_count} onChange={v => save('window_count', v)} placeholder="12" />
            </Field>
            <Field label="Window Type">
              <SELECT value={data.window_type} onChange={v => save('window_type', v)}
                options={[['','— Select —'],['single_pane','Single Pane'],['double_pane','Double Pane'],['impact','Impact'],['other','Other']]} />
            </Field>
            <Field label="Condition">
              <SELECT value={data.window_condition} onChange={v => save('window_condition', v)} options={CONDITION} />
            </Field>
            <Field label="Action">
              <SELECT value={data.window_action} onChange={v => save('window_action', v)}
                options={[['keep','Keep'],['replace_partial','Replace Partial'],['replace_all','Replace All']]} />
            </Field>

            {/* ── Siding ── */}
            <SectionHeader label="Siding" />
            <Field label="Siding Type">
              <SELECT value={data.siding_type} onChange={v => save('siding_type', v)}
                options={[['','— Select —'],['vinyl','Vinyl'],['wood','Wood'],['hardiplank','Hardieplank'],
                  ['stucco','Stucco'],['brick','Brick'],['other','Other']]} />
            </Field>
            <Field label="Condition">
              <SELECT value={data.siding_condition} onChange={v => save('siding_condition', v)} options={CONDITION} />
            </Field>
            <Field label="Action">
              <SELECT value={data.siding_action} onChange={v => save('siding_action', v)} options={ACTION_KEEP} />
            </Field>
            {data.siding_action && data.siding_action !== 'keep' && (
              <Field label="Siding Sq Ft">
                <NumberInput value={data.siding_sq_ft} onChange={v => save('siding_sq_ft', v)} placeholder="1200" />
              </Field>
            )}

            {/* ── Exterior Paint ── */}
            <SectionHeader label="Exterior Paint" />
            <Field label="Paint Condition">
              <SELECT value={data.ext_paint_condition} onChange={v => save('ext_paint_condition', v)} options={CONDITION} />
            </Field>
            <Field label="Paint Action">
              <SELECT value={data.ext_paint_action} onChange={v => save('ext_paint_action', v)}
                options={[['keep','Keep'],['partial','Touch Up / Partial'],['full','Full Repaint']]} />
            </Field>
            {data.ext_paint_action && data.ext_paint_action !== 'keep' && (
              <Field label="Paint Sq Ft">
                <NumberInput value={data.ext_paint_sq_ft} onChange={v => save('ext_paint_sq_ft', v)} placeholder="1800" />
              </Field>
            )}

            {/* ── Gutters ── */}
            <SectionHeader label="Gutters" />
            <Field label="Gutters Action">
              <SELECT value={data.gutters_action || 'none'} onChange={v => save('gutters_action', v)}
                options={[
                  ['none',   'None (No Work)'],
                  ['keep',   'Keep As-Is'],
                  ['repair', 'Repair'],
                  ['replace','Replace'],
                  ['demo',   'Demo / Remove'],
                ]} />
            </Field>
            {data.gutters_action && data.gutters_action !== 'none' && data.gutters_action !== 'demo' && (
              <>
                <Field label="Condition">
                  <SELECT value={data.gutters_condition} onChange={v => save('gutters_condition', v)} options={CONDITION} />
                </Field>
                <Field label="Linear Ft">
                  <NumberInput value={data.gutters_linear_ft} onChange={v => save('gutters_linear_ft', v)} placeholder="150" />
                </Field>
              </>
            )}
            {data.gutters_action === 'demo' && (
              <Field label="Linear Ft (to remove)">
                <NumberInput value={data.gutters_linear_ft} onChange={v => save('gutters_linear_ft', v)} placeholder="150" />
              </Field>
            )}

            {/* ── Pressure Wash ── */}
            <div style={{gridColumn:'span 2',marginTop:8}}>
              <CheckRow label="Pressure wash (driveway / exterior)" checked={data.pressure_wash} onChange={v => save('pressure_wash', v)} />
              {!!data.pressure_wash && (
                <div style={{paddingLeft:24}}>
                  <Field label="Pressure Wash Sq Ft">
                    <NumberInput value={data.pressure_wash_sq_ft} onChange={v => save('pressure_wash_sq_ft', v)} placeholder="2000" />
                  </Field>
                </div>
              )}
            </div>

            {/* ── Notes ── */}
            <div style={{gridColumn:'span 2',marginTop:4}}>
              <Field label="Notes">
                <textarea rows={2} defaultValue={data.notes || ''}
                  onBlur={e => save('notes', e.target.value)}
                  style={{resize:'vertical',width:'100%'}} placeholder="Fascia, soffit, shutters, front door…" />
              </Field>
            </div>

          </div>

          <SOWCostTable dealId={deal.id} lines={costLines} sowCosts={deal.sow_costs} onSynced={onUpdate} />
        </div>
      )}
    </div>
  );
}
